import { useEffect, useRef, type ElementType } from 'react';
import { easeOutCubic } from './motion-value';
import { getScrollContainer, prefersReducedMotion } from './scroll-engine';

export interface CountUpProps {
    /** Final value to count to. */
    end: number;
    /** Starting value (defaults to 0). */
    start?: number;
    /** Tween duration in ms (spec: 1–1.5s). */
    duration?: number;
    /** Decimal places to render. */
    decimals?: number;
    prefix?: string;
    suffix?: string;
    className?: string;
    /** Element tag to render (default `span`). */
    as?: ElementType;
    /** Custom formatter for the running value; overrides decimals/prefix/suffix. */
    format?: (value: number) => string;
}

/**
 * Counts from `start` to `end` with an easeOutCubic tween the first time the
 * element scrolls into view. One-shot (not scrubbed), matching the spec's
 * "counter animates upward" entrances. Respects reduced-motion by snapping to the
 * final value.
 */
export default function CountUp({
    end,
    start = 0,
    duration = 1400,
    decimals = 0,
    prefix = '',
    suffix = '',
    className,
    as: Tag = 'span',
    format,
}: CountUpProps) {
    const ref = useRef<HTMLElement>(null);
    const played = useRef(false);

    const render = (value: number): string => (format ? format(value) : `${prefix}${value.toFixed(decimals)}${suffix}`);

    useEffect(() => {
        const el = ref.current;
        if (!el) return;
        el.textContent = render(start);

        const run = (): void => {
            if (prefersReducedMotion()) {
                el.textContent = render(end);
                return;
            }
            let startTs = 0;
            const step = (ts: number): void => {
                if (!startTs) startTs = ts;
                const p = Math.min((ts - startTs) / duration, 1);
                el.textContent = render(start + (end - start) * easeOutCubic(p));
                if (p < 1) requestAnimationFrame(step);
            };
            requestAnimationFrame(step);
        };

        const io = new IntersectionObserver(
            (entries) => {
                if (entries[0]?.isIntersecting && !played.current) {
                    played.current = true;
                    io.disconnect();
                    run();
                }
            },
            { threshold: 0.4, root: getScrollContainer() },
        );
        io.observe(el);
        return () => io.disconnect();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [end, start, duration, decimals, prefix, suffix]);

    return <Tag ref={ref} className={className} />;
}
